import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Table, Input, Button, Modal, message, Dropdown, Menu, Radio, Tabs, Tooltip } from "antd";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  LoadingOutlined,
  MoreOutlined,
  SearchOutlined,
  ClearOutlined,
  SendOutlined,
  SyncOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { deleteCampaign, getCampaigns } from "../../api/Services";
import {
  handleDownloadCampaignAudienceExport,
  handleDownloadCampaignExport,
} from "../../helpers/Helper";
import PageHeader from "../../components/pageheader/PageHeader";
import styles from "./Campaigns.module.scss";
import moment from "moment";
import CampaignTemplates from "./templates/CampaignTemplates";
import CampaignTemplateCreate from "./templates/CampaignTemplateCreate";
import { RxMagicWand } from "react-icons/rx";
const { TabPane } = Tabs;

const Campaigns = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(
    parseInt(searchParams.get("page") || "1")
  );
  const [nameFilterValue, setNameFilterValue] = useState(
    searchParams.get("name") || ""
  );

  const [sendModalVisible, setSendModalVisible] = useState(false);
  const [sendCampaignId, setSendCampaignId] = useState<number | null>(null);
  const [sendType, setSendType] =
    useState<"EMAIL" | "SMS" | "WHATSAPP">("WHATSAPP");
  const [downloadingExportId, setDownloadingExportId] = useState<number | null>(
    null
  );
  const [downloadingAudienceId, setDownloadingAudienceId] = useState<
    number | null
  >(null);

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/campaigns";
    }
    return "/promotions/campaigns";
  };

  const isBrightActions = location.pathname.startsWith("/commercial-intelligence");
  const showCampaignSendFeatures = !isBrightActions;

  const isTemplatesPath = location.pathname.includes("/templates");
  const isCreateTemplatePath = location.pathname.endsWith("/templates/create");
  const isEditTemplatePath = /\/templates\/\d+\/edit$/.test(location.pathname);
  const tabFromPath =
    showCampaignSendFeatures && isTemplatesPath ? "templates" : "campaigns";
  const [activeTab, setActiveTab] = useState<"campaigns" | "templates">(
    tabFromPath
  );
  useEffect(() => {
    setActiveTab(tabFromPath);
  }, [tabFromPath]);

  useEffect(() => {
    if (isBrightActions && isTemplatesPath) {
      navigate("/commercial-intelligence/campaigns", { replace: true });
    }
  }, [isBrightActions, isTemplatesPath, navigate]);

  const updateURL = useCallback(
    (filters: { name?: string; page?: number }) => {
      const params = new URLSearchParams();

      if (filters.name) params.set("name", filters.name);
      if (filters.page && filters.page > 1)
        params.set("page", filters.page.toString());

      setSearchParams(params);
    },
    [setSearchParams]
  );

  const fetchCampaigns = useCallback(
    async (pagination = actualPage) => {
      setLoading(true);
      try {
        const response = await getCampaigns(pagination);
        setData(response?.campaigns || []);
        setTotalSize(response?.totalElements || 0);
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        setData([]);
        setTotalSize(0);
      } finally {
        setLoading(false);
      }
    },
    [actualPage]
  );

  useEffect(() => {
    if (activeTab === "campaigns") {
      fetchCampaigns();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actualPage, location.pathname, activeTab]);

  useEffect(() => {
    const hasCreating = data.some((c: any) => c.campaignStatus === "CREANDO");
    if (!hasCreating) return;
    const interval = setInterval(() => fetchCampaigns(), 5000);
    return () => clearInterval(interval);
  }, [data, fetchCampaigns]);

  const showDeleteConfirm = (id: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDelete(id);
      },
    });
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    const deleteStatus = await deleteCampaign(id);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await fetchCampaigns();
    setLoading(false);
  };

  const handlePagination = (page: number) => {
    setActualPage(page);
    updateURL({ name: nameFilterValue, page });
    fetchCampaigns(page);
  };

  const handleClearFilters = () => {
    setNameFilterValue("");
    setActualPage(1);
    setSearchParams({});
    fetchCampaigns(1);
  };

  const hasActiveFilters = () => {
    return nameFilterValue !== "";
  };

  const getCampaignViewUrl = (id: number) => {
    const params = new URLSearchParams();
    if (nameFilterValue) params.set("name", nameFilterValue);
    if (actualPage > 1) params.set("page", actualPage.toString());

    const queryString = params.toString();
    return `${getBasePath()}/${id}/view${queryString ? `?${queryString}` : ""}`;
  };

  const renderCampaignType = (campaignType?: string) => {
    if (!campaignType) return "-";
    if (campaignType === "INCENTIVE") return t("Common-CampaignTypeIncentive");
    if (campaignType === "PROMOTION") return t("Common-CampaignTypePromotion");
    if (campaignType === "SEGMENT") return t("Common-CampaignTypeSegment");
    if (campaignType === "ACTIVATION") return t("Common-CampaignTypeActivation");
    return campaignType;
  };

  const renderCampaignStatus = (campaignStatus?: string) => {
    if (!campaignStatus) return "-";
    if (campaignStatus === "CREANDO")
      return (
        <span>
          <SyncOutlined spin style={{ marginRight: 6 }} />
          {t("Common-CampaignStatusCreating")}
        </span>
      );
    if (campaignStatus === "ERROR")
      return <span style={{ color: "#ff4d4f" }}>{t("Common-CampaignStatusError")}</span>;
    if (campaignStatus === "FUTURA") return t("Common-CampaignStatusFuture");
    if (campaignStatus === "ACTIVA") return t("Common-CampaignStatusActive");
    if (campaignStatus === "FINALIZADA") return t("Common-CampaignStatusFinished");
    return campaignStatus;
  };

  const renderCurrency = (value?: number) =>
    value === null || value === undefined ? "-" : `$${value}`;

  const renderNumber = (value?: number) =>
    value === null || value === undefined ? "-" : `${value}`;

  const columns = [
    {
      title: t("Common-Name"),
      dataIndex: "name",
      render: (name: string, record: any) => (
        <a href={getCampaignViewUrl(record.id)} className={styles.nameLink}>
          {name}
        </a>
      ),
    },
    {
      title: t("Common-Type"),
      dataIndex: "campaignType",
      render: (campaignType: string) => renderCampaignType(campaignType),
    },
    {
      title: (
        <span>
          {t("Campaigns-AssignedClients")}{" "}
          <Tooltip title={t("Campaigns-AssignedClientsTooltip")}>
            <QuestionCircleOutlined />
          </Tooltip>
        </span>
      ),
      dataIndex: "clientCount",
      render: (clientCount: number) =>
        clientCount != null ? String(clientCount) : "-",
    },
    {
      title: t("Common-Start"),
      dataIndex: "validityStartDate",
      render: (date: any) => (date ? moment(date).format("DD-MM-YYYY") : "-"),
    },
    {
      title: t("Common-End"),
      dataIndex: "validityEndDate",
      render: (date: any) => (date ? moment(date).format("DD-MM-YYYY") : "-"),
    },
    {
      title: t("Common-Status"),
      dataIndex: "campaignStatus",
      render: (campaignStatus: string) => renderCampaignStatus(campaignStatus),
    },
    {
      title: t("Common-EstimatedImpact"),
      dataIndex: "estimatedImpact",
      render: (value: number) => renderCurrency(value),
    },
    {
      title: t("Common-EstimatedCost"),
      dataIndex: "estimatedCost",
      render: (value: number) => renderCurrency(value),
    },
    {
      title: t("Common-EstimatedROI"),
      dataIndex: "estimatedRoi",
      render: (value: number) => renderNumber(value),
    },
    {
      title: (
        <span>
          {t("Common-EstimatedAudience")}{" "}
          <Tooltip title={t("Campaigns-EstimatedAudienceTooltip")}>
            <QuestionCircleOutlined />
          </Tooltip>
        </span>
      ),
      dataIndex: "estimatedAudience",
      render: (value: number) => renderNumber(value),
    },
    {
      title: t("Common-ConversionPercentage"),
      dataIndex: "conversionPercentage",
      render: (value: number) => renderNumber(value),
    },
    {
      title: t("Common-IncrementalSales"),
      dataIndex: "incrementalSales",
      render: (value: number) => renderCurrency(value),
    },
    {
      title: t("Common-Cost"),
      dataIndex: "cost",
      render: (value: number, record: any) =>
        !record.reward ? t("Common-NotApplicableNoIncentive") : renderCurrency(value),
    },
    {
      title: t("Common-ROI"),
      dataIndex: "roi",
      render: (value: number, record: any) =>
        !record.reward ? t("Common-NotApplicableNoIncentive") : renderNumber(value),
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() => navigate(getCampaignViewUrl(record.id))}
              >
                {t("Common-View")}
              </Menu.Item>
              {hasPermission(privilegesEnum.WRITE_CAMPAIGNS) && (
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() => navigate(getCampaignViewUrl(record.id))}
                >
                  {t("Common-Edit")}
                </Menu.Item>
              )}
              <Menu.Item
                key="viewSents"
                icon={<EyeOutlined />}
                onClick={() =>
                  navigate(`${getBasePath()}/${record.id}/sent`)
                }
              >
                {t("Common-ViewSends")}
              </Menu.Item>
              <Menu.Item
                key="download"
                icon={
                  downloadingExportId === record.id ? (
                    <LoadingOutlined />
                  ) : (
                    <DownloadOutlined />
                  )
                }
                onClick={async () => {
                  const hideLoading = message.loading(
                    t("Common-Downloading"),
                    0
                  );
                  setDownloadingExportId(record.id);
                  try {
                    const success = await handleDownloadCampaignExport(
                      record.id,
                      () => {},
                      record.name
                    );
                    hideLoading();
                    if (success) {
                      message.success(t("Common-DownloadSuccessMessage"));
                    } else {
                      message.error(t("Common-ErrorMessage"));
                    }
                  } catch {
                    hideLoading();
                    message.error(t("Common-ErrorMessage"));
                  } finally {
                    setDownloadingExportId(null);
                  }
                }}
                disabled={downloadingExportId === record.id}
              >
                {t("Common-DownloadCampaignData")}
              </Menu.Item>
              <Menu.Item
                key="downloadAudience"
                icon={
                  downloadingAudienceId === record.id ? (
                    <LoadingOutlined />
                  ) : (
                    <DownloadOutlined />
                  )
                }
                onClick={async () => {
                  const hideLoading = message.loading(
                    t("Common-Downloading"),
                    0
                  );
                  setDownloadingAudienceId(record.id);
                  try {
                    const success = await handleDownloadCampaignAudienceExport(
                      record.id,
                      () => {},
                      record.name
                    );
                    hideLoading();
                    if (success) {
                      message.success(t("Common-DownloadSuccessMessage"));
                    } else {
                      message.error(t("Common-ErrorMessage"));
                    }
                  } catch {
                    hideLoading();
                    message.error(t("Common-ErrorMessage"));
                  } finally {
                    setDownloadingAudienceId(null);
                  }
                }}
                disabled={downloadingAudienceId === record.id}
              >
                {t("Common-DownloadAudience")}
              </Menu.Item>
              {showCampaignSendFeatures &&
                hasPermission(privilegesEnum.SEND_CAMPAIGNS) && (
                <Menu.Item
                  key="send"
                  icon={<SendOutlined />}
                  onClick={() => {
                    setSendCampaignId(record.id);
                    setSendType("WHATSAPP");
                    setSendModalVisible(true);
                  }}
                >
                  {t("Common-SendCampaign")}
                </Menu.Item>
              )}
              <Menu.Item
                key="delete"
                icon={<DeleteOutlined />}
                onClick={() => showDeleteConfirm(record.id)}
              >
                {t("Common-Delete")}
              </Menu.Item>
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <>
      <div className={styles.wrapper}>
        <PageHeader
          title={t("NavBar-Campaigns")}
          icon={<RxMagicWand />}
          onCreateClick={() => navigate(`${getBasePath()}/create`)}
          showCreateButton={activeTab === "campaigns"}
        />
        <div className={styles.tabsWrapper}>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              const next = key === "templates" ? "templates" : "campaigns";
              setActiveTab(next);
              navigate(
                next === "templates"
                  ? `${getBasePath()}/templates`
                  : getBasePath()
              );
            }}
            type="card"
            className={styles.tabsCard}
            tabBarStyle={{ marginLeft: 10, marginTop: 20 }}
          >
            <TabPane tab={t("NavBar-Campaigns")} key="campaigns" />
            {showCampaignSendFeatures && (
              <TabPane tab={t("Common-WhatsAppTemplates")} key="templates" />
            )}
          </Tabs>
        </div>
        <div className={styles.contentWrapper}>
          {activeTab === "campaigns" || !showCampaignSendFeatures ? (
            <div className={styles.tableWrapper}>
              <div className={styles.headerTableWrapper}>
                <div className={styles.filtersWrapper}>
                  <div className={styles.filterWrapper}>
                    <Input
                      placeholder={t("Common-SearchByName")}
                      className={styles.filterInput}
                      prefix={<SearchOutlined />}
                      allowClear
                      onChange={(e) => {
                        setNameFilterValue(e.target.value);
                        setActualPage(1);
                        updateURL({ name: e.target.value, page: 1 });
                      }}
                      value={nameFilterValue}
                    />
                  </div>
                  {hasActiveFilters() && (
                    <Button
                      className={styles.clearButton}
                      icon={<ClearOutlined />}
                      onClick={handleClearFilters}
                    >
                      {t("Common-ClearFilters")}
                    </Button>
                  )}
                </div>
              </div>
              <Table
                size="small"
                className={styles.table}
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={{
                  current: actualPage,
                  pageSize: 10,
                  total: totalSize,
                  onChange: handlePagination,
                  showSizeChanger: false,
                }}
                rowKey="id"
              />
            </div>
          ) : (
            (isCreateTemplatePath || isEditTemplatePath) ? <CampaignTemplateCreate /> : <CampaignTemplates />
          )}
        </div>
      </div>
      {showCampaignSendFeatures && (
      <Modal
        visible={sendModalVisible}
        title={t("Common-SendCampaign")}
        onCancel={() => setSendModalVisible(false)}
        onOk={() => {
          if (sendCampaignId != null) {
            navigate(
              `${getBasePath()}/${sendCampaignId}/send?type=${sendType}`
            );
            setSendModalVisible(false);
          }
        }}
        okText={t("Common-Next")}
        cancelText={t("Common-Cancel")}
      >
        <p>{t("Common-ChannelType")}:</p>
        <Radio.Group
          value={sendType}
          onChange={(e) => setSendType(e.target.value)}
        >
          <Radio value="EMAIL">{t("Common-ChannelTypeEmail")}</Radio>
          <Radio value="SMS">{t("Common-ChannelTypeSMS")}</Radio>
          <Radio value="WHATSAPP">{t("Common-ChannelTypeWhatsApp")}</Radio>
        </Radio.Group>
      </Modal>
      )}
    </>
  );
};

export default Campaigns;
